import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { EditIcon, SegmentControl } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { Socials } from '@root/entities/socials'
import { CurrencyBanner } from '@root/features/currency/ui/currency-banner/currency-banner'
import { useBreakpoint } from '@root/shared/hooks/useBreakpoint'
import { mediaMinTablet } from '@root/shared/ui-kit'

import type { CurrencyModel } from '@root/entities/currency'
import type { PairModel } from '@root/entities/pair'
import type { PremiumMetaModel } from '@root/entities/premium'

type Tab = 'logo' | 'banner'

type Props = {
  currency: CurrencyModel
  pair: PairModel | undefined
  premium: PremiumMetaModel
  onEditClick: () => void
  onBannerEditClick: () => void
}

export const PremiumCurrencyScreenCurrencyImages: React.FC<Props> = ({
  currency,
  pair,
  premium,
  onEditClick,
  onBannerEditClick,
}) => {
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'mobile'
  const [tab, setTab] = useState<Tab>('logo')

  const [banner] = useAtom(premium.banner)

  const renderBanner = () => {
    if (!pair) {
      return undefined
    }

    return (
      <BannerArea>
        <CurrencyBanner
          premium={true}
          socials={Socials.empty()}
          banner={banner}
          owner={undefined}
          pair={pair}
          archiveButtonSlot={undefined}
          footerSlot={undefined}
        />
        <Edit onClick={onBannerEditClick}>
          <EditIcon size={isMobile ? 16 : 18} />
        </Edit>
      </BannerArea>
    )
  }

  return (
    <Root>
      <Tabs value={tab} onChange={setTab} size="h30">
        <SegmentControl.Item value={'logo' satisfies Tab}>
          Logo
        </SegmentControl.Item>
        <SegmentControl.Item value={'banner' satisfies Tab}>
          Banner
        </SegmentControl.Item>
      </Tabs>
      <LogoArea>
        <Background>
          <Line />
        </Background>
        {tab === 'logo' ? (
          <CurrencyEdit>
            <CurrencyIcon
              key={currency.address}
              currency={currency}
              size={isMobile ? 100 : 150}
              onPremiumFallbackClick={onEditClick}
            />
            <Edit onClick={onEditClick}>
              <EditIcon size={isMobile ? 16 : 18} />
            </Edit>
          </CurrencyEdit>
        ) : (
          renderBanner()
        )}
      </LogoArea>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  justify-items: center;
  gap: 16px;
  width: 100%;
`

const Tabs = styled(SegmentControl<Tab>)``

const LogoArea = styled.div`
  display: grid;
  position: relative;
  place-items: center;

  width: 100%;
  min-height: 170px;
`

const BannerArea = styled.div`
  display: grid;
  position: relative;
  place-items: center;
  width: min-content;
`

const Background = styled.div`
  position: absolute;
  align-content: end;
  pointer-events: none;
  top: 0;
  width: 100%;
  overflow: hidden;

  height: 86px;
  background-image: radial-gradient(
    40% 80px at 50% 110%,
    var(--subscriptionGradientFrom-9) 30%,
    var(--subscriptionGradientTo-9) 70%,
    transparent 120%
  );

  ${mediaMinTablet(css`
    height: 136px;

    background-image: radial-gradient(
      40% 140px at 50% 110%,
      var(--subscriptionGradientFrom-9) 30%,
      var(--subscriptionGradientTo-9) 70%,
      transparent 100%
    );
  `)}
`

const Line = styled.span`
  display: grid;

  height: 1px;
  width: 393px;
  opacity: 0.15;

  background: linear-gradient(
    90deg,
    rgba(var(--background-12-rgb), 0.2) 0%,
    var(--background-12) 50%,
    rgba(var(--background-12-rgb), 0.2) 100%
  );
`

const CurrencyEdit = styled.div`
  display: grid;
  position: relative;
  place-self: center;
`

const Edit = styled.div`
  background: var(--backgroundA-6);
  backdrop-filter: blur(30px);

  transition: background 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    background: var(--backgroundA-7);
  }

  align-items: center;
  justify-content: center;
  display: flex;

  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;

  flex-shrink: 0;
  border-radius: 50%;

  ${mediaMinTablet(css`
    width: 40px;
    height: 40px;
  `)}
`
